import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Eye, EyeOff, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const user = await login(formData.username, formData.password);
      toast.success(`${t("welcome")}, ${user.full_name || user.username}!`);
      
      // Redirect based on role
      switch (user.role) {
        case "admin":
        case "staff":
          navigate("/admin");
          break;
        case "driver":
          navigate("/driver");
          break;
        default:
          navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t("loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
          {t("backToHome")}
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-0">
            <img 
              src={LOGO_URL} 
              alt="بحر الرمال" 
              className="w-20 h-20 mx-auto mb-4 rounded-full object-cover border-4 border-[#38bdf8]"
            />
            <CardTitle className="font-heading text-2xl">{t("welcomeBack")}</CardTitle>
            <CardDescription>{t("signInToAccount")}</CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div>
                <Label htmlFor="username" className="mb-2 block">{t("username")}</Label>
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t("username")}
                  required
                  className="h-12"
                  data-testid="login-username"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="mb-2 block">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("password")}
                    required
                    className="h-12 pr-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90 h-12"
                data-testid="login-submit"
              >
                {isLoading ? t("loading") : t("signIn")}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {t("dontHaveAccount")}{" "}
              <Link to="/register" className="text-[#1a365d] hover:underline font-medium">
                {t("register")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;
